/**
 * asset_store.ts — ONE loader for every decoded clip (2026-09-20). Its I/O is
 * injected (fetch, decode, clock, sleep, rng), so the whole state machine runs
 * in node under fault injection.
 *
 * What it replaces — four private caches (sfx, ambience, music, the engine's
 * zones) that all shared two defects:
 *
 *   A MISS WAS FOREVER. Any failed fetch or decode stored `null` for the key
 *   and was never retried that session — a single dropped packet at login
 *   silenced a weapon until the next reload. A 404 SHOULD be permanent (the law
 *   "a missing clip is silence, never an error" lets wiring land before its
 *   audio); a 503, a timeout or a reset connection should not.
 *
 *   NOTHING WAS EVER FREED. Every decoded music track (~19 MB of PCM each, 12 of
 *   them) stayed for the session. Decoded size is frames × channels × 4 bytes,
 *   whatever the MP3 weighs.
 *
 * States:  absent → loading → ready
 *                          ↘ retry-wait → loading …   (transient: backoff + jitter, 4 tries)
 *                          ↘ failed(permanent)        (404 / 403 / 410 / undecodable)
 *                          ↘ failed(transient)        (tries exhausted — expires after NEGATIVE_TTL_MS)
 *
 * Concurrent askers share ONE in-flight load (singleflight). Eviction is LRU
 * over UNPINNED entries only, and never an entry touched in the last few
 * seconds — a buffer that is playing is pinned by its voice.
 */

export type AssetState = 'absent' | 'loading' | 'retry-wait' | 'ready' | 'failed';

export interface FetchResult { status: number; bytes: ArrayBuffer | null; retryAfterMs?: number }

export interface AssetStoreDeps<B> {
  /** Resolve an asset id to a URL. */
  resolve(id: string): string;
  /** status 0 = network error / timeout (transient). Must not throw. */
  fetchBytes(url: string): Promise<FetchResult>;
  /** Decode complete file bytes; throws when the bytes are not audio. */
  decode(bytes: ArrayBuffer): Promise<B>;
  /** Decoded size of a buffer in bytes. */
  sizeOf(buffer: B): number;
  now(): number;
  sleep(ms: number): Promise<void>;
  rng(): number;
}

export const MAX_ATTEMPTS = 4;
export const BACKOFF_BASE_MS = 250;
export const BACKOFF_MAX_MS = 4000;
/** A transient failure that exhausted its tries may be asked for again after this. */
export const NEGATIVE_TTL_MS = 60_000;
/** Never evict what was used this recently (a loop between two `set()` calls). */
export const EVICT_GRACE_MS = 8000;
/**
 * A decoded buffer under this is a ONE-SHOT (≈ 2.7 s of mono at 48 kHz): the kind whose first play must not be late, and
 * the kind that frees almost nothing when it goes. Evicted only after every large idle buffer has gone.
 */
export const SMALL_ASSET_BYTES = 512 * 1024;
/** The one-shot pool's own ceiling, as a share of the budget: it answers to THIS, never to what the beds and the score weigh. */
export const SMALL_POOL_SHARE = 0.5;

interface Entry<B> {
  state: AssetState;
  buffer: B | null;
  bytes: number;
  pins: number;
  lastUsed: number;
  attempts: number;
  permanent: boolean;
  failedAt: number;
  lastStatus: number;
  inflight: Promise<B | null> | null;
}

/** HTTP statuses that will not get better by asking again this session. */
export function isPermanentStatus(status: number): boolean {
  return status === 400 || status === 401 || status === 403 || status === 404 || status === 410 || status === 415;
}

/** Exponential backoff with full jitter in the upper half; honours Retry-After. */
export function backoffMs(attempt: number, rng: number, retryAfterMs?: number): number {
  const exp = Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * 2 ** attempt);
  const jittered = exp * (0.5 + 0.5 * Math.min(1, Math.max(0, rng)));
  return Math.max(jittered, Math.min(BACKOFF_MAX_MS * 2, retryAfterMs ?? 0));
}

export class AudioAssetStore<B> {
  private readonly entries = new Map<string, Entry<B>>();
  private budgetBytes: number;
  totalBytes = 0;
  readonly stats = { fetches: 0, retries: 0, decoded: 0, permanentFailures: 0, transientFailures: 0, evicted: 0, decodeMsTotal: 0 };

  constructor(private readonly deps: AssetStoreDeps<B>, budgetMiB: number) { this.budgetBytes = budgetMiB * 1024 * 1024; }

  setBudgetMiB(m: number): void { this.budgetBytes = m * 1024 * 1024; this.evict(); }

  state(id: string): AssetState {
    const e = this.entries.get(id);
    if (!e) return 'absent';
    if (e.state === 'failed' && !e.permanent && this.deps.now() - e.failedAt >= NEGATIVE_TTL_MS) return 'absent';
    return e.state;
  }

  /** true = decoded · false = will not play this session (permanent) · null = not known yet (starts the load). */
  available(id: string): boolean | null {
    const e = this.entries.get(id);
    if (e?.state === 'ready') return true;
    if (e?.state === 'failed' && e.permanent) return false;
    void this.load(id);
    return null;
  }

  /** The decoded buffer if it is ready NOW (never waits); otherwise starts the load and returns null. */
  get(id: string): B | null {
    const e = this.entries.get(id);
    if (e?.state === 'ready' && e.buffer) { e.lastUsed = this.deps.now(); return e.buffer; }
    void this.load(id);
    return null;
  }

  /** Singleflight load. Resolves null on failure — never rejects (fire-and-forget callers). */
  load(id: string): Promise<B | null> {
    let e = this.entries.get(id);
    if (e) {
      if (e.state === 'ready') { e.lastUsed = this.deps.now(); return Promise.resolve(e.buffer); }
      if (e.inflight) return e.inflight;
      if (e.state === 'failed') {
        if (e.permanent || this.deps.now() - e.failedAt < NEGATIVE_TTL_MS) return Promise.resolve(null);
        e.attempts = 0; // the negative entry expired — a fresh set of tries
      }
    } else {
      e = { state: 'absent', buffer: null, bytes: 0, pins: 0, lastUsed: this.deps.now(), attempts: 0, permanent: false, failedAt: 0, lastStatus: 0, inflight: null };
      this.entries.set(id, e);
    }
    const entry = e;
    entry.inflight = this.run(id, entry).finally(() => { entry.inflight = null; });
    return entry.inflight;
  }

  private async run(id: string, e: Entry<B>): Promise<B | null> {
    const url = this.deps.resolve(id);
    for (;;) {
      e.state = 'loading';
      e.attempts++;
      this.stats.fetches++;
      const res = await this.deps.fetchBytes(url);
      e.lastStatus = res.status;
      if (res.status >= 200 && res.status < 300 && res.bytes) {
        try {
          const t0 = this.deps.now();
          const buf = await this.deps.decode(res.bytes);
          this.stats.decodeMsTotal += this.deps.now() - t0;
          e.buffer = buf;
          e.bytes = this.deps.sizeOf(buf);
          e.state = 'ready';
          e.lastUsed = this.deps.now();
          this.totalBytes += e.bytes;
          this.stats.decoded++;
          this.evict(id);
          return buf;
        } catch {
          // The server answered 200 with bytes that are not audio: asking again returns the same bytes.
          return this.fail(e, true);
        }
      }
      if (isPermanentStatus(res.status)) return this.fail(e, true);
      if (e.attempts >= MAX_ATTEMPTS) return this.fail(e, false);
      e.state = 'retry-wait';
      this.stats.retries++;
      await this.deps.sleep(backoffMs(e.attempts - 1, this.deps.rng(), res.retryAfterMs));
    }
  }

  private fail(e: Entry<B>, permanent: boolean): null {
    e.state = 'failed';
    e.permanent = permanent;
    e.failedAt = this.deps.now();
    if (permanent) this.stats.permanentFailures++; else this.stats.transientFailures++;
    return null;
  }

  /** Load a group; resolves when every member has settled. */
  async preload(ids: readonly string[]): Promise<{ ready: string[]; failed: string[] }> {
    const out = { ready: [] as string[], failed: [] as string[] };
    await Promise.all(ids.map(async (id) => { (await this.load(id)) ? out.ready.push(id) : out.failed.push(id); }));
    return out;
  }

  /** A playing voice pins its buffer; pinned entries are never evicted. Returns the unpin. */
  pin(id: string): () => void {
    const e = this.entries.get(id);
    if (!e) return () => { /* unknown */ };
    e.pins++;
    let done = false;
    return () => { if (!done) { done = true; e.pins = Math.max(0, e.pins - 1); e.lastUsed = this.deps.now(); } };
  }

  /** A new manifest / version for an id: forget what we knew (a permanent failure included). */
  invalidate(id: string): void {
    const e = this.entries.get(id);
    if (!e || e.inflight) return;
    if (e.state === 'ready') this.totalBytes -= e.bytes;
    this.entries.delete(id);
  }

  /**
   * TWO POOLS. Large buffers (beds, the score) answer to the byte budget: unpinned and idle, oldest first, until it
   * holds. One-shots (< SMALL_ASSET_BYTES) answer ONLY to their own share of it. `keep` is never evicted.
   *
   * A plain LRU threw out exactly the wrong things: whatever was decoded at unlock — the critical clips, every footstep
   * take — carries the OLDEST stamp in the cache, so the first bed across the budget evicted sixty 40 KB clips to free
   * 3 MB. And "small ones as a last resort" was no better: on the cheap tiers what is PLAYING (a stereo track and three
   * beds, all pinned) can outweigh the budget by itself, so the sweep took every one-shot and STILL could not balance
   * the books — 92 evictions, 57.7 MiB of 48, and 39 of 41 footfalls on their stand-in (the live probe, 2026-09-20).
   */
  private evict(keep?: string): void {
    const now = this.deps.now();
    const idle = (small: boolean): Array<[string, Entry<B>]> => [...this.entries.entries()]
      .filter(([id, e]) => id !== keep && e.state === 'ready' && e.pins === 0 && now - e.lastUsed >= EVICT_GRACE_MS && (e.bytes < SMALL_ASSET_BYTES) === small)
      .sort((a, b) => a[1].lastUsed - b[1].lastUsed);
    const drop = (id: string, e: Entry<B>): void => { this.totalBytes -= e.bytes; this.entries.delete(id); this.stats.evicted++; };
    if (this.totalBytes > this.budgetBytes) for (const [id, e] of idle(false)) { if (this.totalBytes <= this.budgetBytes) break; drop(id, e); }
    let smallBytes = 0;
    for (const e of this.entries.values()) if (e.state === 'ready' && e.bytes < SMALL_ASSET_BYTES) smallBytes += e.bytes;
    const smallCap = this.budgetBytes * SMALL_POOL_SHARE;
    if (smallBytes > smallCap) for (const [id, e] of idle(true)) { if (smallBytes <= smallCap) break; smallBytes -= e.bytes; drop(id, e); }
  }

  diag(): { entries: number; ready: number; loading: number; failed: string[]; decodedMiB: number; budgetMiB: number; stats: AudioAssetStore<B>['stats'] } {
    let ready = 0, loading = 0;
    const failed: string[] = [];
    for (const [id, e] of this.entries) {
      if (e.state === 'ready') ready++;
      else if (e.state === 'loading' || e.state === 'retry-wait') loading++;
      else if (e.state === 'failed') failed.push(`${id}:${e.permanent ? e.lastStatus || 'undecodable' : `transient(${e.lastStatus})`}`);
    }
    return { entries: this.entries.size, ready, loading, failed, decodedMiB: +(this.totalBytes / 1048576).toFixed(1), budgetMiB: +(this.budgetBytes / 1048576).toFixed(0), stats: this.stats };
  }
}
